import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckoutContext, CheckoutContextProps } from '../context/CheckoutContext';
import { DfcAuthOverlayStep, useDfcAuthOverlay } from '../context/DfcAuthOverlayContext';
import { RegistrationStep } from '../Utils/constants';
import { rememberPostAuthReturn } from '../Utils/postAuthReturn';

/**
 * The Fitness Village theme's auth entry point — Sign in, as used by the header, the mobile
 * menu and the home's account card, so all three behave identically. The overlay it opens
 * carries its own Create account tab.
 *
 * The current page is remembered first (`rememberPostAuthReturn`), so any flow that does leave
 * it — email verification — comes back here. Then the overlay opens in place; without an
 * overlay provider the visitor goes to the matching `/Register` step, as before.
 */
const useDfcAuthEntry = () => {
  const navigate = useNavigate();
  const authOverlay = useDfcAuthOverlay();
  const { company, selectedSession } = useContext(CheckoutContext) as CheckoutContextProps;

  const openAuth = useCallback(
    (registrationStep: DfcAuthOverlayStep) => {
      rememberPostAuthReturn(selectedSession);
      if (authOverlay) {
        authOverlay.openAuth(registrationStep);
        return;
      }
      navigate(`/Register?companyId=${company?.id}`, { state: { registrationStep } });
    },
    [authOverlay, company?.id, navigate, selectedSession]
  );

  const handleSignIn = useCallback(() => openAuth(RegistrationStep.Login), [openAuth]);

  return { handleSignIn };
};

export default useDfcAuthEntry;
